import type { MagicRecipe } from '../encounter-creation-engine/ft3d-magic-vfx';

export const MAGIC_MISSILE_PARENT = '9419C815-6848-4572-4976-6F6E7B8D1D2F';
export const MAGIC_MISSILE_CHILD = '0C25137F-B688-8D55-50E9-9B580781FD5D';
export const MAGIC_EFFECT_CAPABILITY = 'ft.magic-effect.v1';
export interface MagicTake { audioId: string; revision: string; url: string; format: string; sha256: string }
export interface MagicVariant { id: string; revision: string; recipe: MagicRecipe; impactOffsetMs: number; durationMs: number }
export interface MagicEffectCatalog {
  schema: 'ft.magic-catalog.v1'; catalogRevision: string; itemId: string;
  variants: MagicVariant[]; impact: MagicTake[];
}
export interface MagicProjectileSelection {
  projectileIndex: number; vfxVariantId: string; vfxRevision: string;
  audioId: string; audioRevision: string;
}
export interface MagicSelection {
  schema: 'ft.magic-selection.v1'; requestId: string; catalogRevision: string; projectiles: MagicProjectileSelection[];
}
export interface MagicEffect extends MagicProjectileSelection {
  type: 'ft3d_magic_effect'; schema: 'ft.magic-effect.v1'; eventId: string; effectCastId: string;
  encounterId: string; sourceEntityId: string; targetEntityId: string; catalogRevision: string;
  publishedAtUnixMs: number; startAtUnixMs: number; impactOffsetMs: number; durationMs: number; expiresAtUnixMs: number;
}
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const hash = /^[0-9a-f]{64}$/;
export function magicCatalog(value: unknown): MagicEffectCatalog | null {
  if (!value || typeof value !== 'object') return null;
  const c = value as MagicEffectCatalog;
  if (c.schema !== 'ft.magic-catalog.v1' || c.itemId !== MAGIC_MISSILE_CHILD || !hash.test(c.catalogRevision)
    || !Array.isArray(c.variants) || !c.variants.length || c.variants.length > 8
    || !Array.isArray(c.impact) || !c.impact.length || c.impact.length > 16) return null;
  if (c.variants.some(v => !v.id || !hash.test(v.revision) || v.recipe?.kind !== 'projectile'
    || !Number.isFinite(v.durationMs) || v.durationMs < 200 || v.durationMs > 8000
    || v.impactOffsetMs <= 0 || v.impactOffsetMs >= v.durationMs)) return null;
  if (c.impact.some(a => !uuid.test(a.audioId) || !hash.test(a.revision) || !hash.test(a.sha256)
    || !/^https:\/\//.test(a.url) || !['mp3','wav','ogg','webm'].includes(a.format))) return null;
  return c;
}
/** Call once when consuming an action-owned draft; reuse this object for request recovery. */
export function selectMagicProjectiles(catalog: MagicEffectCatalog, count: number,
  random: () => number = Math.random, requestId: string = crypto.randomUUID()): MagicSelection {
  if (!magicCatalog(catalog) || !Number.isInteger(count) || count < 1 || count > 1000 || !uuid.test(requestId)) throw new Error('Magic Missile presentation is unavailable. Refresh before casting.');
  const pick = <T>(items: T[]): T => items[Math.min(items.length - 1, Math.max(0, Math.floor(random() * items.length)))]!;
  return { schema: 'ft.magic-selection.v1', requestId, catalogRevision: catalog.catalogRevision,
    projectiles: Array.from({ length: count }, (_, projectileIndex) => {
      const variant = pick(catalog.variants), audio = pick(catalog.impact);
      return { projectileIndex, vfxVariantId: variant.id, vfxRevision: variant.revision, audioId: audio.audioId, audioRevision: audio.revision };
    }) };
}
export function resolveMagicEffect(event: MagicEffect, catalog: MagicEffectCatalog): { variant: MagicVariant; audio: MagicTake } | null {
  if (event?.type !== 'ft3d_magic_effect' || event.schema !== MAGIC_EFFECT_CAPABILITY
    || !uuid.test(event.effectCastId) || !Number.isInteger(event.projectileIndex) || event.projectileIndex < 0
    || event.eventId !== `${event.effectCastId}:${event.projectileIndex}`
    || ![event.encounterId,event.sourceEntityId,event.targetEntityId].every(id => uuid.test(id))
    || event.catalogRevision !== catalog.catalogRevision) return null;
  const variant = catalog.variants.find(v => v.id === event.vfxVariantId && v.revision === event.vfxRevision);
  const audio = catalog.impact.find(a => a.audioId === event.audioId && a.revision === event.audioRevision);
  if (!variant || !audio || event.impactOffsetMs !== variant.impactOffsetMs || event.durationMs !== variant.durationMs
    || ![event.startAtUnixMs,event.publishedAtUnixMs,event.expiresAtUnixMs].every(Number.isFinite)
    || event.startAtUnixMs < event.publishedAtUnixMs || event.startAtUnixMs > event.publishedAtUnixMs + 2000
    || event.expiresAtUnixMs !== event.startAtUnixMs + event.durationMs) return null;
  return { variant, audio };
}
/** Midpoint clock sample; retain the lowest RTT sample to limit asymmetric queuing error. */
export function magicClockSample(sent: number, received: number, serverUnixMs: number): { offsetMs: number; rttMs: number } | null {
  const rttMs = received - sent;
  return [sent,received,serverUnixMs].every(Number.isFinite) && rttMs >= 0 && rttMs <= 2000
    ? { offsetMs: serverUnixMs - (sent + received) / 2, rttMs } : null;
}
